import React, { useMemo, useState, useEffect, useCallback } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/layouts/tabsContainerStyle';
import { makeStyles } from '@material-ui/styles';
import { BodyM, HeadingMBold } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import TabsWithDots from 'components/TabsWithDots/TabsWithDots';
import EmptyTab from './EmptyTab';
import classNames from 'classnames';
import Button from 'components/CustomButtons/Button';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { EshopProductsService, getFullDetailsNameOfDeviceByPartNumber } from 'services/eshop/products';
import { throwError } from 'rxjs';
import { ICartItem, IInsuranceCartItem } from 'utils/api/models/eshop/viewCart';
import moment from 'utils/date/moment';
import BulkNumbersList from '../subcomponents/BulkNumbersList';
import SelectNumbersList from '../subcomponents/SelectNumbersList';
import { SingleAccount } from 'utils/api/models/accounts/singleAccount';
import { AccountsService } from 'services/accounts/accounts';
import { finalize } from 'rxjs/operators';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { DepartmentsService } from 'services/departments/departments';
import PaymentTab from './PaymentTab';
import SummaryTab from './SummaryTab';
import { useHistory } from 'react-router-dom';
import { ProductsService } from 'services/products/products';
import { AccountType } from 'utils/api/models/accounts/accountType';
import { store } from 'redux/store';
import { useDispatch } from 'react-redux';
import { IGuestLogIn } from 'utils/api/models/eshop/guestLogIn';
import { updateHeaders } from 'redux/modules/settings/headers/actions';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
export interface SingleNumber extends SingleAccount {
	disabled?: boolean;
}

// @ts-ignore
const useStyles = makeStyles(styles);

enum STEPS {
	NUMBERS = 0,
	DEVICE = 1,
	PAYMENT = 2,
	SUMMARY = 3,
}

export type BulkNumber = {
	accountNumber: string | undefined;
	accountType?: AccountType | undefined;
	billingType?: string | undefined;
	tariffName?: string | undefined;
	discount?: number | string | undefined;
	accountId?: string | undefined;
	commitmentDate?: string | undefined;
	device?: string | undefined;
	currentPrice?: string | undefined;
	prediscountPrice?: string | undefined;
	orderItemId?: string | undefined;
	productParentId?: string | undefined;
	partNumber?: string | undefined;
};

const BulkPurchaseDevicesIndex = () => {
	const classes = useStyles();
	const history = useHistory();
	const [activeTab, setActiveTab] = useState<number>(STEPS.NUMBERS);
	const [initialScreen, setInitialScreen] = useState<boolean>(true);
	const [cartNumbers, setCartNumbers] = useState<BulkNumber[]>([]);
	const [externalNumbers, setExternalNumbers] = useState<SingleNumber[]>([]);
	const [loadingNumbers, setLoadingNumbers] = useState<boolean>(false);
	const [tableLoading, setTableLoading] = useState<boolean>(false);
	const [loadingCart, setLoadingCart] = useState<boolean>(true);
	const [departments, setDepartments] = useState<SingleDepartment[]>([]);
	const [hasUnavailableDevices, setHasUnavailableDevices] = useState<boolean>(false);
	const [lastSavedToken, setLastSavedToken] = useState<string | undefined>();
	const [orderId, setOrderId] = useState('');
	const [orderIsSuccessful, setOrderIsSuccessful] = useState(false);
	const dispatch = useDispatch();

	const isAnyNumberHasNoDevice = useCallback(() => {
		const anyNumberWithoutDevice = cartNumbers.find((num: BulkNumber) => !num?.orderItemId);
		return !anyNumberWithoutDevice;
	}, [cartNumbers]);

	const isStepValid: boolean = useMemo(() => {
		switch (activeTab) {
			case STEPS.NUMBERS:
				return cartNumbers.length > 0;
			case STEPS.DEVICE:
				return !!cartNumbers?.length && isAnyNumberHasNoDevice();
			case STEPS.PAYMENT:
				return true;
			case STEPS.SUMMARY:
				return true;
			default:
				return false;
		}
	}, [activeTab, cartNumbers, isAnyNumberHasNoDevice]);

	const nextFn = () => {
		switch (activeTab) {
			case STEPS.SUMMARY:
				break;
			default:
				if (isStepValid) {
					setActiveTab((activeTab) => activeTab + 1);
				}
				break;
		}
	};

	const backFn = () => {
		switch (activeTab) {
			case STEPS.NUMBERS:
				break;
			case STEPS.PAYMENT:
				refreshCart();
				break;
			default:
				setActiveTab((activeTab) => activeTab - 1);
				break;
		}
	};

	const refreshCart = () => {
		setLoadingCart(true);
		EshopProductsService.getOmantelIdentity().subscribe(
			(data: IGuestLogIn) => {
				dispatch(
					updateHeaders({
						WCTrustedToken: data?.WCTrustedToken,
						WCToken: data?.WCToken,
						WCPersonalization: data?.personalizationID,
					})
				);
				EshopProductsService.viewCart(data?.WCTrustedToken, data?.WCToken, data?.personalizationID)
					.pipe(
						finalize(() => {
							setLoadingCart(false);
						})
					)
					.subscribe(
						({ items }) => {
							const filteredNumbers = cartNumbers.filter((cnum) =>
								items.find((item) => item?.['mobilePhone'] === cnum?.accountNumber)
							);
							setCartNumbers(filteredNumbers);
							getAdditionalNumbersData(filteredNumbers);
							setActiveTab((activeTab) => activeTab - 1);
						},
						(error) => {
							throwError(error);
							setCartNumbers([]);
							getAdditionalNumbersData([]);
							setActiveTab((activeTab) => activeTab - 2);
						}
					);
			},
			(error: any) => {
				throwError(error);
			}
		);
	};

	const parseCartData = (items: (ICartItem | IInsuranceCartItem)[]) => {
		const filteredNumbers = cartNumbers.filter(
			(cnum) => !items.find((item) => item?.['mobilePhone'] === cnum?.accountNumber)
		);

		const item = (items || []).find((item: any) => item?.PA !== 'B2B_WITH_VOUCHER_UPFRONT');
		setHasUnavailableDevices(!!item);

		const cartMappedNumbers = (items || []).map((item: ICartItem | IInsuranceCartItem) => {
			const matchingCartNumber = cartNumbers.find((cnum) => item['mobilePhone'] === cnum.accountNumber);
			return {
				accountNumber: item?.['mobilePhone'],
				tariffName: matchingCartNumber?.tariffName || item?.ebuPlanName || '-',
				commitmentDate: moment(item?.['createDate'])
					.add(Number(item?.['commitmentDuration']), 'M')
					.format('DD.MM.YYYY'),
				device: item?.name,
				discount: item?.discount,
				currentPrice: item?.['totalAmount'],
				prediscountPrice: item?.['standardPrice'],
				orderItemId: item?.orderItemId,
				partNumber: item?.partNumber,
				productParentId: item?.productParentId,
			} as BulkNumber;
		});

		const allCartNumbers = filteredNumbers.concat(cartMappedNumbers);
		setCartNumbers(allCartNumbers);
		getAdditionalNumbersData(allCartNumbers);
	};

	const fetchCartData = useCallback(() => {
		setLoadingCart(true);
		EshopProductsService.getOmantelIdentity().subscribe((data: IGuestLogIn) => {
			dispatch(
				updateHeaders({
					WCTrustedToken: data?.WCTrustedToken,
					WCToken: data?.WCToken,
					WCPersonalization: data?.personalizationID,
				})
			);
			EshopProductsService.viewCart(data?.WCTrustedToken, data?.WCToken, data?.personalizationID)
				.pipe(
					finalize(() => {
						setLoadingCart(false);
					})
				)
				.subscribe(
					({ items }) => {
						parseCartData(items);
					},
					(error) => {
						throwError(error);
					}
				);
		});
	}, [cartNumbers]);

	const getAdditionalNumbersData = (allCartNumbers: BulkNumber[]) => {
		allCartNumbers.forEach((number) => {
			if (number?.orderItemId) {
				EshopProductsService.getProductById({
					productId: number?.productParentId,
					itemType: 'ITEM',
				}).subscribe(
					({ items }) => {
						const productName = getFullDetailsNameOfDeviceByPartNumber(
							number?.partNumber || '',
							number?.device || '',
							items || []
						);
						setCartNumbers((cartNumbers) => {
							return cartNumbers.map((cartNumber) => {
								if (cartNumber.accountNumber === number.accountNumber) {
									return {
										...cartNumber,
										device: productName,
									};
								} else {
									return cartNumber;
								}
							});
						});
					},
					(error) => throwError(error)
				);
			}
		});
	};

	const removeCartItems = useCallback(() => {
		setLoadingCart(true);
		EshopProductsService.clearAllCartItems()
			.pipe(
				finalize(() => {
					setLoadingCart(false);
					setInitialScreen(false);
					setActiveTab(STEPS.NUMBERS);
				})
			)
			.subscribe(
				() => {},
				(error) => {
					throwError(error);
				}
			);
	}, []);

	useEffect(() => {
		DepartmentsService.getDepartmentsForCurrentUser().subscribe(({ departments }) => {
			setDepartments(departments.filter((department) => department.deptId));
		});
	}, []);
	const headers = store.getState()?.settings?.headers;

	useEffect(() => {
		if (lastSavedToken !== headers.WCPersonalization && headers.WCPersonalization !== '') {
			setLastSavedToken(headers.WCPersonalization);
			fetchCartData();
		}
	}, [headers.WCPersonalization, fetchCartData, lastSavedToken, setLastSavedToken]);

	const fetchTariffForNumberAndAddToCart = (bulkNumber: BulkNumber, discount?: number) => {
		if (!bulkNumber.tariffName && bulkNumber.accountNumber && bulkNumber.accountType && bulkNumber.billingType) {
			setTableLoading(true);
			ProductsService.getCustomerProducts({
				phoneNumber: bulkNumber.accountNumber,
				accountType: bulkNumber.accountType,
				billingType: bulkNumber.billingType,
				resourceInfo: true,
			})
				.pipe(finalize(() => setTableLoading(false)))
				.subscribe((res) => {
					const tariffName = res.tariffPlan(bulkNumber.accountType as AccountType)?.name || '-';
					setCartNumbers((oldNumbers: BulkNumber[]) =>
						oldNumbers.concat([{ ...bulkNumber, tariffName, discount }])
					);
				});
		}
	};

	const cacheFormattedCartNumbers = cartNumbers
		.filter((cartNumber) => cartNumber.accountNumber)
		.map((cartNumber) => {
			return { number: cartNumber.accountNumber as string, tariffName: cartNumber.tariffName || '' };
		});

	const changeSelected = (clickedNumber: SingleNumber, changedToSelected: boolean, discount?: number) => {
		const bulkNumber = {
			accountNumber: clickedNumber?.accountNum,
			accountType: clickedNumber?.accountType,
			billingType: clickedNumber?.billingType,
			accountId: clickedNumber?.bAccountId,
			tariffName: undefined,
		} as BulkNumber;
		if (changedToSelected) {
			fetchTariffForNumberAndAddToCart(bulkNumber, discount);
		} else {
			deleteNumbersFromCart([bulkNumber]);
		}
	};

	const deleteNumbersFromCart = (numbersToDelete: BulkNumber[]) => {
		setCartNumbers((oldCartNumbers) => {
			return oldCartNumbers.filter((cartNumber) =>
				numbersToDelete.find((numberToDelete) => numberToDelete.accountNumber !== cartNumber.accountNumber)
			);
		});

		const numbersToSave = cacheFormattedCartNumbers.filter((cartNumber) =>
			numbersToDelete.find((numberToDelete) => numberToDelete.accountNumber !== cartNumber.number)
		);
	};

	const setDisableNumber = (numb: SingleNumber) => {
		setExternalNumbers((oldNumbers) =>
			oldNumbers.map((oldNumb) => {
				if (oldNumb.accountNum === numb.accountNum) {
					return { ...oldNumb, disabled: true };
				}
				return oldNumb;
			})
		);
		setTableLoading(false);
	};

	useEffect(() => {
		if (activeTab === STEPS.NUMBERS) {
			setLoadingNumbers(true);
			AccountsService.getCorpUserNumbersWithChilds()
				.pipe(
					finalize(() => {
						setLoadingNumbers(false);
					})
				)
				.subscribe(
					({ accounts: numbersFromServer }) => {
						if (numbersFromServer) {
							setExternalNumbers(
								numbersFromServer.map((number) => {
									return {
										...number,
										disabled: false,
									};
								})
							);
						}
					},
					(error: any) => throwError(error)
				);
		}
	}, [activeTab]);

	useEffect(() => {
		const returningUserHasItemsInCart =
			cartNumbers.length > 0 && activeTab === STEPS.NUMBERS && initialScreen === true;
		const lastDeviceRemovedFromCart = cartNumbers.length === 0 && activeTab === STEPS.DEVICE;
		if (returningUserHasItemsInCart) {
			setActiveTab(STEPS.DEVICE);
			setInitialScreen(false);
		} else if (lastDeviceRemovedFromCart) {
			setActiveTab(STEPS.NUMBERS);
		}
	}, [cartNumbers, setActiveTab, activeTab]);

	const headerElement = () => {
		if (!initialScreen && activeTab === STEPS.NUMBERS) {
			return (
				<div>
					<HeadingMBold color="secondary-light">
						{translate('purchase.bulk.devices.title.select.number')}
					</HeadingMBold>
					<BodyM color="secondary-light">{translate('purchase.bulk.devices.subheader.select.number')}</BodyM>
				</div>
			);
		} else if (activeTab === STEPS.DEVICE) {
			return (
				<div>
					<HeadingMBold color="secondary-light">
						{translate('purchase.bulk.devices.subheader.add.device')}
					</HeadingMBold>
					<BodyM color="secondary-light">{translate('purchase.bulk.devices.subheader.add.device')}</BodyM>
				</div>
			);
		} else {
			return <></>;
		}
	};

	return (
		<div className={classes.container}>
			{headerElement()}

			<ConditionalRender
				show={!initialScreen}
				onTrue={() => (
					<TabsWithDots
						tabs={[
							{
								name: translate('purchase.bulk.devices.title.select.number'),
								content: (
									<SelectNumbersList
										numbersLoading={loadingNumbers}
										numbersData={externalNumbers}
										changeSelected={changeSelected}
										cartNumbers={cartNumbers}
										setDisableNumber={setDisableNumber}
										departments={departments}
										tableLoading={tableLoading}
										setTableLoading={setTableLoading}
									/>
								),
							},
							{
								name: translate('purchase.bulk.devices.title.add.device'),
								content: (
									<BulkNumbersList
										numbers={cartNumbers}
										deleteNumbersFromCart={deleteNumbersFromCart}
										fetchCartData={fetchCartData}
										backFn={backFn}
									/>
								),
							},
							{
								name: translate('purchase.bulk.devices.tab.payment'),
								content: (
									<PaymentTab
										nextFn={nextFn}
										setOrderId={setOrderId}
										setOrderIsSuccessful={setOrderIsSuccessful}
									/>
								),
							},
							{
								name: translate('purchase.bulk.devices.tab.summary'),
								content: (
									<SummaryTab
										numbersCount={cartNumbers.length}
										requestNumber={orderId}
										orderIsSuccessful={orderIsSuccessful}
										onClick={() => {
											history.push('/dashboard/summary');
										}}
									/>
								),
							},
						]}
						activeTab={activeTab}
						setActiveTab={setActiveTab}
					/>
				)}
				onFalse={() => (
					<EmptyTab
						loading={loadingCart}
						onClick={() => {
							if (hasUnavailableDevices) {
								removeCartItems();
							} else {
								setInitialScreen(false);
								setActiveTab(STEPS.NUMBERS);
							}
						}}
						cancelScreen={hasUnavailableDevices}
						onClickCancel={() => {
							history.push('/dashboard');
						}}
					/>
				)}
			/>

			<div
				className={classNames({
					[classes.tabsButtons]: true,
				})}
			>
				<div className={classes.right}>
					<ConditionalRender
						show={cartNumbers.length > 0 && activeTab === STEPS.DEVICE}
						onTrue={() => {
							return (
								<Button
									color="primary-light"
									className={classes.nextButton}
									disabled={!isStepValid}
									onClick={nextFn}
								>
									{translate('purchase.bulk.devices.go.to.cart')}
								</Button>
							);
						}}
					/>
					<ConditionalRender
						show={activeTab === STEPS.NUMBERS && !initialScreen}
						onTrue={() => {
							return (
								<>
									<Button
										color="tertiary-light"
										onClick={() => {
											history.push('/dashboard');
										}}
										disabled={false}
									>
										{translate('cancel')}
									</Button>
									<Button
										color="primary-light"
										className={classes.nextButton}
										disabled={!isStepValid}
										onClick={nextFn}
									>
										{cartNumbers?.length
											? translate('add.count', cartNumbers.length)
											: translate('next')}
									</Button>
								</>
							);
						}}
					/>
				</div>
				<div className={classes.right}>
					<ConditionalRender
						show={activeTab !== STEPS.NUMBERS && activeTab !== STEPS.SUMMARY && !initialScreen}
						onTrue={() => {
							return (
								<Button color="tertiary-light" onClick={backFn} disabled={false}>
									{translate('back')}
								</Button>
							);
						}}
					/>
				</div>
			</div>
		</div>
	);
};

export default BulkPurchaseDevicesIndex;
